import { useState, useEffect } from 'react';

interface CookieData {
  title_line1: string;
  title_line2: string;
  title_line3: string;
  description_paragraph1: string;
  description_paragraph2: string;
  cookie_image1: string;
  cookie_image2: string;
  cookie_image3: string;
}

export default function CookieSection() {
  const [data, setData] = useState<CookieData>({
    title_line1: 'SABOROSA,',
    title_line2: 'CROCANTE E',
    title_line3: 'AÇORIANA!',
    description_paragraph1: 'A Bolacha Mulata é um verdadeiro ícone dos Açores, reconhecida pelo seu inconfundível sabor a cacau e pela textura intensamente crocante. Produzida no arquipélago com uma receita única e inalterada ao longo do tempo, distingue-se pelo equilíbrio perfeito entre sabor e simplicidade.',
    description_paragraph2: 'Cada bolacha reflete a tradição e a identidade açoriana, tornando-se um produto autêntico e imediatamente reconhecível, apreciado por gerações.',
    cookie_image1: '/Imagens/BOLACHA_MULATA_1.png',
    cookie_image2: '/Imagens/BOLACHA_MULATA_2.png',
    cookie_image3: '/Imagens/BOLACHA_MULATA_3.png'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch('https://mulata.pt/bolacha-mulata/api/cookie.php');
      const result = await response.json();
      if (result) {
        setData(result);
      }
    } catch (err) {
      console.error('Error loading cookie data:', err);
    }
  };

  const getImageSrc = (path: string) => {
    if (path.startsWith('http')) {
      return path;
    }
    return path;
  };

  // Check if content contains HTML tags
  const isHtml = (text: string) => /<[a-z][\s\S]*>/i.test(text);

  const formatDesktopText = (text: string) => {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    const wordsPerLine = 13;

    words.forEach((word, index) => {
      currentLine += word + ' ';
      if ((index + 1) % wordsPerLine === 0 || index === words.length - 1) {
        lines.push(currentLine.trim());
        currentLine = '';
      }
    });

    return lines;
  };

  const renderParagraph = (text: string, style: React.CSSProperties, isDesktop: boolean) => {
    if (isHtml(text)) {
      // Render HTML content directly (supports links, bold, etc.)
      return (
        <div
          style={style}
          className="cookie-rich-content"
          dangerouslySetInnerHTML={{ __html: text }}
        />
      );
    }

    // Plain text rendering
    if (isDesktop) {
      const lines = formatDesktopText(text);
      return (
        <p style={style}>
          {lines.map((line, index) => (
            <span key={index}>
              {line}
              {index < lines.length - 1 && <br />}
            </span>
          ))}
        </p>
      );
    }

    return <p style={style}>{text}</p>;
  };

  // Desktop: 3.5vw
  const titleSize = '3.5vw';
  // Mobile: 8vw * 1.25 = 10vw
  const titleSizeMobile = '10vw';

  return (
    <>
      {/* Cookie Section - Desktop */}
      <section 
        className="cookie-section-desktop relative" 
        style={{ 
          backgroundImage: 'url(https://i.imgur.com/qEOWcbL.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          width: '100%',
          paddingBottom: '57.29%',
          position: 'relative',
          height: 0,
          imageRendering: '-webkit-optimize-contrast'
        }}
      >
        <div 
          className="absolute inset-0 flex flex-col items-center justify-center"
          style={{
            paddingTop: '4.17vw',
            paddingBottom: '4.17vw'
          }}
        >
          <h2 
            className="text-center prompt-black-font whitespace-nowrap" 
            style={{ 
              color: '#562310', 
              letterSpacing: '0.05em',
              fontSize: titleSize,
              marginBottom: '2.08vw'
            }}
          >
            {data.title_line1} {data.title_line2} {data.title_line3}
          </h2>

          <div 
            className="flex items-center justify-center"
            style={{
              gap: '1.25vw',
              marginBottom: '2.08vw'
            }}
          >
            <img
              src={getImageSrc(data.cookie_image1)}
              alt="Bolacha Mulata"
              className="w-auto animate-float"
              style={{ height: '16.93vw', animationDelay: '0s' }}
              onError={(e) => {
                e.currentTarget.src = '/Imagens/BOLACHA_MULATA_1.png';
              }}
            />
            <img
              src={getImageSrc(data.cookie_image2)}
              alt="Bolacha Mulata"
              className="w-auto animate-float"
              style={{ height: '24.00vw', animationDelay: '0.3s' }}
              onError={(e) => {
                e.currentTarget.src = '/Imagens/BOLACHA_MULATA_2.png';
              }}
            />
            <img
              src={getImageSrc(data.cookie_image3)}
              alt="Bolacha Mulata"
              className="w-auto animate-float"
              style={{ height: '16.93vw', animationDelay: '0.6s' }}
              onError={(e) => {
                e.currentTarget.src = '/Imagens/BOLACHA_MULATA_3.png';
              }}
            />
          </div>

          <div 
            className="text-center rethink-bold-font px-4" 
            style={{ 
              color: '#673723',
              maxWidth: '72.92vw'
            }}
          >
            {renderParagraph(data.description_paragraph1, {
              fontSize: '1.56vw',
              lineHeight: '1.5',
              marginBottom: '0.78vw'
            }, true)}
            {renderParagraph(data.description_paragraph2, {
              fontSize: '1.56vw',
              lineHeight: '1.5'
            }, true)}
          </div>
        </div>
      </section>

      {/* Cookie Section - Mobile - 600x1430px (238.33% ratio) */}
      <section 
        className="cookie-section-mobile relative" 
        style={{ 
          backgroundImage: 'url(https://i.imgur.com/iQIN26p.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          width: '100%',
          paddingBottom: '238.33%',
          position: 'relative',
          height: 0,
          imageRendering: '-webkit-optimize-contrast'
        }}
      >
        <div 
          className="absolute w-full px-6 flex flex-col items-center"
          style={{
           top: '-58.5vw',
            paddingBottom: '8vw'
          }}
        >
          <h2 
            className="text-center prompt-black-font" 
            style={{ 
              color: '#562310', 
              letterSpacing: '0.05em', 
              lineHeight: '1.2', 
              marginBottom: '5vw',
              marginTop: '30vw',
              fontSize: titleSizeMobile,
              fontWeight: 900
            }}
          >
            {data.title_line1}<br />{data.title_line2}<br />{data.title_line3}
          </h2>

          <div 
            className="flex flex-col items-center w-full" 
            style={{ 
              gap: '-2vw',
              marginBottom: '5vw',
              marginTop: '0vw'
            }}
          >
            <div className="w-full flex justify-start" style={{ paddingLeft: '12vw' }}>
              <img
                src={getImageSrc(data.cookie_image1)}
                alt="Bolacha Mulata"
                className="w-auto animate-float"
                style={{ height: '44vw', animationDelay: '0s' }}
                onError={(e) => {
                  e.currentTarget.src = '/Imagens/BOLACHA_MULATA_1.png';
                }}
              />
            </div>
            <div className="w-full flex justify-center" style={{ marginTop: '-5vw', paddingLeft: '8vw' }}>
              <img
                src={getImageSrc(data.cookie_image2)}
                alt="Bolacha Mulata"
                className="w-auto animate-float"
                style={{ height: '68.2vw', animationDelay: '0.3s', zIndex: 2 }}
                onError={(e) => {
                  e.currentTarget.src = '/Imagens/BOLACHA_MULATA_2.png';
                }}
              />
            </div>
            <div className="w-full flex justify-end" style={{ paddingRight: '12vw', marginTop: '-5vw' }}>
              <img
                src={getImageSrc(data.cookie_image3)}
                alt="Bolacha Mulata"
                className="w-auto animate-float"
                style={{ height: '44vw', animationDelay: '0.6s' }}
                onError={(e) => {
                  e.currentTarget.src = '/Imagens/BOLACHA_MULATA_3.png';
                }}
              />
            </div>
          </div>

          <div 
            className="text-center rethink-bold-font px-4" 
            style={{ 
              color: '#673723', 
              marginTop: '0vw'
            }}
          >
            {renderParagraph(data.description_paragraph1, {
              fontSize: '4.5vw',
              lineHeight: '1.3',
              marginBottom: '4vw'
            }, false)}
            {renderParagraph(data.description_paragraph2, {
              fontSize: '4.5vw',
              lineHeight: '1.3'
            }, false)}
          </div>
        </div>
      </section>
    </>
  );
}
